import React from "react";
import {
  getRoomShapeDefinition,
  RoomShapeId,
  ROOM_SHAPE_IDS,
  RoomShapeParams,
} from "./roomShapeTemplates";

interface RoomShapeSilhouetteProps {
  shapeId: RoomShapeId;
  params?: RoomShapeParams;
  className?: string;
  /**
   * Extra viewBox padding as a fraction of the larger shape dimension.
   * Higher values zoom the silhouette out (default 0.08 for grid thumbnails).
   */
  paddingRatio?: number;
}

/**
 * SVG silhouette for a room shape, fitted into a unit viewBox.
 * No stroke or corner handles — selection is indicated by the grid ring instead.
 */
export const RoomShapeSilhouette: React.FC<RoomShapeSilhouetteProps> = ({
  shapeId,
  params,
  className = "",
  paddingRatio = 0.08,
}) => {
  const definition = getRoomShapeDefinition(shapeId);
  const vertices = definition.getVertices(params ?? definition.defaultParams);
  const xs = vertices.map((v) => v.x);
  const ys = vertices.map((v) => v.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const w = Math.max(1, maxX - minX);
  const h = Math.max(1, maxY - minY);
  const pad = Math.max(w, h) * paddingRatio;
  const points = vertices
    .map((v) => `${v.x - minX},${maxY - v.y}`)
    .join(" ");

  return (
    <svg
      viewBox={`${-pad} ${-pad} ${w + pad * 2} ${h + pad * 2}`}
      className={className}
      aria-hidden
    >
      <polygon points={points} fill="#D4D4D4" />
    </svg>
  );
};

interface RoomShapeGridProps {
  selectedId: RoomShapeId;
  onSelect: (id: RoomShapeId) => void;
  labels: Record<RoomShapeId, string>;
}

export const RoomShapeGrid: React.FC<RoomShapeGridProps> = ({
  selectedId,
  onSelect,
  labels,
}) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
    {ROOM_SHAPE_IDS.map((id) => {
      const selected = id === selectedId;
      return (
        <button
          key={id}
          type="button"
          onClick={() => onSelect(id)}
          className="flex flex-col items-center gap-2 rounded-lg p-2 text-center transition-colors hover:bg-black/5"
        >
          <div
            className={`flex h-24 w-full items-center justify-center rounded-md ${
              selected ? "ring-2 ring-black" : ""
            }`}
          >
            <RoomShapeSilhouette shapeId={id} className="h-20 w-20" />
          </div>
          <span className="text-xs font-medium text-gray-900">
            {labels[id]}
          </span>
        </button>
      );
    })}
  </div>
);
